import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
    Collapse,
    LinearProgress,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    Button,
    Tooltip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloseIcon from '@mui/icons-material/Close';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { useOcrJobs } from '../../Contexts/OcrJobsContext';
import { useDirectionByLanguage } from '../../hooks/useDirectionByLanguage';
import Styles from './OcrJobProgress.module.css';

const AUTO_HIDE_DELAY = 5000;
const ISRAEL_TZ = 'Asia/Jerusalem';
const STUCK_AFTER_MINUTES = 180;

// Server timestamps without an offset are UTC.
const parseServerDate = (value) => {
    if (!value) return null;
    const text = String(value);
    const date = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(text) ? text : `${text}Z`);
    return Number.isNaN(date.getTime()) ? null : date;
};

const formatIsraelDateTime = (value) => {
    const date = parseServerDate(value);
    if (!date) return null;
    try {
        const parts = new Intl.DateTimeFormat('en-GB', {
            timeZone: ISRAEL_TZ, day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
        }).formatToParts(date);
        const get = (type) => parts.find(p => p.type === type)?.value ?? '';
        return `${get('day')}/${get('month')}/${get('year')} ${get('hour')}:${get('minute')}`;
    } catch {
        return null;
    }
};

const getAgeMinutes = (job) => {
    const created = parseServerDate(job.createdAt);
    if (created) return Math.max(0, Math.floor((Date.now() - created.getTime()) / 60000));
    return job.ageMinutes ?? null;
};

const formatElapsed = (minutes, t) => {
    if (minutes == null) return null;
    if (minutes < 60) return `${minutes}${t('timeMinutes')}`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}${t('ocrTimeHours')} ${minutes % 60}${t('timeMinutes')}`;
    return `${Math.floor(hours / 24)}${t('ocrTimeDays')} ${hours % 24}${t('ocrTimeHours')}`;
};

const getEntityLabel = (job, t) => {
    const entity = (job.entityType || '').toLowerCase();
    if (job.type === 'pa-ocr' || entity.includes('payment')) return t('ocrEntityPaymentAcceptance');
    if (entity === 'income') return t('ocrEntityIncome');
    if (entity === 'expense') return t('ocrEntityExpense');
    return job.entityType || null;
};

const formatTime = (seconds, t) => {
    if (seconds == null || seconds <= 0) return null;
    const m = t('timeMinutes');
    const s = t('timeSeconds');
    if (seconds < 60) return `~${seconds}${s}`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs > 0 ? `~${mins}${m} ${secs}${s}` : `~${mins}${m}`;
};

function OcrJobProgress() {
    const { i18n, t } = useTranslation();
    const direction = useDirectionByLanguage(i18n);
    const { jobs, clearCompletedJobs, cancelJob } = useOcrJobs();

    const [expanded, setExpanded] = useState(false);
    const [visible, setVisible] = useState(false);
    const autoHideTimer = useRef(null);
    const [confirmJob, setConfirmJob] = useState(null); // job pending cancel-confirmation
    const [cancellingId, setCancellingId] = useState(null); // job currently being cancelled (in-flight)
    const [openDetails, setOpenDetails] = useState({});

    const toggleDetails = (jobId) => setOpenDetails(prev => ({ ...prev, [jobId]: !prev[jobId] }));

    const pending = useMemo(() => jobs.filter(j => j.status === 'Pending').length, [jobs]);
    const completed = useMemo(() => jobs.filter(j => j.status === 'Completed').length, [jobs]);
    const failed = useMemo(() => jobs.filter(j => j.status === 'Failed').length, [jobs]);
    const total = jobs.length;
    const allDone = total > 0 && pending === 0;
    const progress = total > 0 ? ((completed + failed) / total) * 100 : 0;

    const maxEstimate = useMemo(() => {
        const estimates = jobs
            .filter(j => j.status === 'Pending' && j.estimatedSeconds != null)
            .map(j => j.estimatedSeconds);
        return estimates.length > 0 ? Math.max(...estimates) : null;
    }, [jobs]);

    useEffect(() => {
        if (total > 0) {
            setVisible(true);
            if (autoHideTimer.current) {
                clearTimeout(autoHideTimer.current);
                autoHideTimer.current = null;
            }
        }
    }, [total]);

    useEffect(() => {
        if (allDone && total > 0) {
            if (failed > 0) {
                setExpanded(true);
            } else {
                setExpanded(false);
                autoHideTimer.current = setTimeout(() => {
                    setVisible(false);
                    clearCompletedJobs();
                }, AUTO_HIDE_DELAY);
            }
        }
        return () => {
            if (autoHideTimer.current) clearTimeout(autoHideTimer.current);
        };
    }, [allDone, total, failed, clearCompletedJobs]);

    const handleDismiss = () => {
        setVisible(false);
        setExpanded(false);
        if (allDone) clearCompletedJobs();
    };

    const handleRequestCancel = (job) => {
        if (!job || job.status !== 'Pending') return;
        setConfirmJob(job);
    };

    const handleConfirmCancel = async () => {
        if (!confirmJob) return;
        const job = confirmJob;
        setCancellingId(job.id);
        try {
            const result = await cancelJob(job.id);
            if (result.ok) {
                const deleted = result?.body?.deletedEntitiesCount ?? 0;
                if (deleted > 0) {
                    toast.success(t('ocrJobCancelledWithDocs', { count: deleted, defaultValue: 'Job cancelled and {{count}} document(s) deleted' }));
                } else {
                    toast.success(t('ocrJobCancelled', { defaultValue: 'Job cancelled' }));
                }
            } else if (result.status === 409) {
                toast.warning(t('ocrJobCancelConflict', { defaultValue: 'Job has already completed; cannot cancel.' }));
            } else if (result.status === 404) {
                toast.info(t('ocrJobCancelNotFound', { defaultValue: 'Job no longer exists.' }));
            } else {
                toast.error(t('ocrJobCancelFailed', { defaultValue: 'Failed to cancel job. Please try again.' }));
            }
        } finally {
            setCancellingId(null);
            setConfirmJob(null);
        }
    };

    const handleAbortConfirm = () => {
        if (cancellingId) return; // don't dismiss while in-flight
        setConfirmJob(null);
    };

    if (!visible || total === 0) return null;

    const containerClass = direction === 'rtl' ? Styles.containerRtl : Styles.containerLtr;
    const chipClass = allDone
        ? (failed > 0 ? Styles.chipHasFailed : Styles.chipDone)
        : Styles.chip;

    const estimateText = formatTime(maxEstimate, t);
    const chipLabel = allDone
        ? (failed > 0
            ? t('ocrJobsPartialFail', { completed, failed })
            : t('ocrJobsComplete'))
        : estimateText
            ? `${t('ocrJobsProcessing', { count: pending })} ${estimateText}`
            : t('ocrJobsProcessing', { count: pending });

    const getStatusBadge = (job) => {
        if (job.status === 'Completed') {
            return <span className={Styles.statusBadgeCompleted}>{t('ocrJobCompleted')}</span>;
        }
        if (job.status === 'Failed') {
            return <span className={Styles.statusBadgeFailed}>{t('ocrJobFailed')}</span>;
        }
        const label = job.statusLabel === 'Queued' ? t('ocrJobQueued') : t('ocrJobPending');
        const eta = formatTime(job.estimatedSeconds, t);
        return <span className={Styles.statusPending}>{eta ? `${label} ${eta}` : label}</span>;
    };

    const getJobIcon = (job) => {
        if (job.status === 'Completed') return <CheckCircleIcon sx={{ fontSize: 18, color: '#2e7d32' }} />;
        if (job.status === 'Failed') return <ErrorIcon sx={{ fontSize: 18, color: '#d32f2f' }} />;
        return <CircularProgress size={16} thickness={5} sx={{ color: '#304fff' }} />;
    };

    const getTypeIcon = (job) => {
        if (job.type === 'pa-ocr') return <ReceiptLongOutlinedIcon sx={{ fontSize: 15, color: '#8e8ea0' }} />;
        return <DescriptionOutlinedIcon sx={{ fontSize: 15, color: '#8e8ea0' }} />;
    };

    const renderJobDetails = (job) => {
        const createdText = job.createdAtIsrael || formatIsraelDateTime(job.createdAt);
        const updatedText = job.updatedAtIsrael || formatIsraelDateTime(job.updatedAt);
        const ageMinutes = getAgeMinutes(job);
        const isStuck = job.status === 'Pending'
            && (job.isStuck || (ageMinutes != null && ageMinutes >= STUCK_AFTER_MINUTES));
        const open = Boolean(openDetails[job.id]);
        const fileNames = Array.isArray(job.fileNames) ? job.fileNames.filter(Boolean) : [];
        const entityLabel = getEntityLabel(job, t);

        const rows = [
            { label: t('ocrJobIdLabel'), value: job.id, mono: true },
            entityLabel && { label: t('ocrJobTypeLabel'), value: entityLabel },
            {
                label: t('ocrJobFilesLabel'),
                value: fileNames.length > 0 ? `${job.fileCount || fileNames.length} · ${fileNames.join(', ')}` : job.fileCount,
            },
            { label: t('ocrJobElapsedLabel'), value: formatElapsed(ageMinutes, t) },
            updatedText && { label: t('ocrJobLastUpdateLabel'), value: updatedText, ltr: true },
            job.requeueCount > 0 && { label: t('ocrJobRecoveriesLabel'), value: job.requeueCount },
            job.errorMessage && { label: t('ocrJobErrorLabel'), value: job.errorMessage },
        ].filter(row => row && row.value != null && row.value !== '');

        return (
            <>
                <div className={Styles.jobMeta}>
                    {createdText ? (
                        <span>
                            {t('ocrJobCreatedAt')}: <span dir="ltr">{createdText}</span>
                            <span className={Styles.tzHint}> ({t('ocrJobIsraelTime')})</span>
                        </span>
                    ) : <span />}
                    <button type="button" className={Styles.detailsToggle} onClick={() => toggleDetails(job.id)}>
                        {open ? t('ocrJobHideDetails') : t('ocrJobShowDetails')}
                    </button>
                </div>
                {isStuck && <div className={Styles.stuckWarning}>{t('ocrJobStuckWarning')}</div>}
                <Collapse in={open} timeout={200} unmountOnExit>
                    <div className={Styles.jobDetails}>
                        {rows.map(row => (
                            <React.Fragment key={row.label}>
                                <span className={Styles.detailLabel}>{row.label}</span>
                                <span className={row.mono ? Styles.detailMono : Styles.detailValue} dir={row.mono || row.ltr ? 'ltr' : undefined}>
                                    {row.value}
                                </span>
                            </React.Fragment>
                        ))}
                    </div>
                </Collapse>
            </>
        );
    };

    return (
        <div className={containerClass}>
            <Collapse in={expanded} timeout={300}>
                <div className={Styles.panel}>
                    <div className={Styles.panelHeader}>
                        <span className={Styles.panelTitle}>{t('ocrJobProgressTitle')}</span>
                        <CloseIcon
                            fontSize="small"
                            sx={{ cursor: 'pointer', color: '#aaa', '&:hover': { color: '#555' }, transition: 'color 0.2s' }}
                            onClick={handleDismiss}
                        />
                    </div>

                    {jobs.length > 0 && jobs[0].clientName && (
                        <div className={Styles.metaInfo}>
                            <span><strong>{jobs[0].clientName}</strong></span>
                            <span className={Styles.metaSeparator} />
                            <span>{jobs[0].caseId}</span>
                            {jobs[0].reportingMonth && (
                                <>
                                    <span className={Styles.metaSeparator} />
                                    <span>{jobs[0].reportingMonth}</span>
                                </>
                            )}
                        </div>
                    )}

                    <div className={Styles.progressBar}>
                        <LinearProgress
                            variant="determinate"
                            value={progress}
                            sx={{
                                height: 5,
                                borderRadius: 3,
                                backgroundColor: '#eef0f8',
                                '& .MuiLinearProgress-bar': {
                                    borderRadius: 3,
                                    background: failed > 0
                                        ? 'linear-gradient(90deg, #ff6d00, #ff9100)'
                                        : allDone
                                            ? 'linear-gradient(90deg, #2e7d32, #43a047)'
                                            : 'linear-gradient(90deg, #304fff, #536dfe)',
                                    transition: 'transform 0.6s ease',
                                },
                            }}
                        />
                    </div>

                    <div className={Styles.jobList}>
                        {jobs.map(job => (
                            <div key={job.id} className={Styles.jobRow}>
                                <div className={Styles.jobItem}>
                                    {getJobIcon(job)}
                                    {getTypeIcon(job)}
                                    <span className={Styles.jobId}>
                                        {job.clientName && <strong>{job.clientName}</strong>}
                                        {job.clientName && ' - '}
                                        {job.type === 'pa-ocr' ? t('PaymentAcceptance') : t('Document')}
                                    </span>
                                    {getStatusBadge(job)}
                                    {job.status === 'Pending' && (
                                        <Tooltip title={t('ocrJobCancelTooltip', { defaultValue: 'Cancel this job' })} arrow>
                                            <span>
                                                {cancellingId === job.id ? (
                                                    <CircularProgress size={14} thickness={5} sx={{ color: '#d32f2f', ml: 0.5 }} />
                                                ) : (
                                                    <CancelOutlinedIcon
                                                        sx={{
                                                            fontSize: 18,
                                                            color: '#9e9e9e',
                                                            cursor: 'pointer',
                                                            ml: 0.5,
                                                            transition: 'color 0.15s',
                                                            '&:hover': { color: '#d32f2f' },
                                                        }}
                                                        onClick={() => handleRequestCancel(job)}
                                                    />
                                                )}
                                            </span>
                                        </Tooltip>
                                    )}
                                </div>
                                {job.status === 'Pending' && job.fileCount > 0 && (
                                    <div className={Styles.jobFileProgress}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={job.progressPercent || 0}
                                            sx={{
                                                height: 4,
                                                borderRadius: 2,
                                                flex: 1,
                                                backgroundColor: '#eef0f8',
                                                '& .MuiLinearProgress-bar': {
                                                    borderRadius: 2,
                                                    background: 'linear-gradient(90deg, #304fff, #536dfe)',
                                                    transition: 'transform 0.6s ease',
                                                },
                                            }}
                                        />
                                        <span className={Styles.fileCountText}>
                                            {job.filesProcessed || 0}/{job.fileCount}
                                        </span>
                                    </div>
                                )}
                                {job.status === 'Completed' && job.fileCount > 0 && (
                                    <div className={Styles.jobFileProgress}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={100}
                                            sx={{
                                                height: 4,
                                                borderRadius: 2,
                                                flex: 1,
                                                backgroundColor: '#eef0f8',
                                                '& .MuiLinearProgress-bar': {
                                                    borderRadius: 2,
                                                    background: 'linear-gradient(90deg, #2e7d32, #43a047)',
                                                },
                                            }}
                                        />
                                        <span className={Styles.fileCountDone}>
                                            {job.fileCount}/{job.fileCount}
                                        </span>
                                    </div>
                                )}
                                {renderJobDetails(job)}
                            </div>
                        ))}
                    </div>

                    <div className={Styles.summary}>
                        <span className={Styles.summaryCount}>
                            {completed + failed}/{total} {t('ocrJobProgressTitle').toLowerCase()}
                        </span>
                        {allDone && (
                            <button className={Styles.clearBtn} onClick={handleDismiss}>
                                {t('Dismiss')}
                            </button>
                        )}
                    </div>
                </div>
            </Collapse>

            <div className={chipClass} onClick={() => setExpanded(prev => !prev)}>
                {!allDone && <CircularProgress size={16} thickness={5} sx={{ color: '#fff' }} />}
                {allDone && failed === 0 && <CheckCircleIcon sx={{ fontSize: 18 }} />}
                {allDone && failed > 0 && <ErrorIcon sx={{ fontSize: 18 }} />}
                <span style={{ flex: 1 }}>{chipLabel}</span>
                <ExpandLessIcon
                    fontSize="small"
                    className={expanded ? Styles.expandIconOpen : Styles.expandIcon}
                />
            </div>

            <Dialog
                open={Boolean(confirmJob)}
                onClose={handleAbortConfirm}
                dir={direction}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 700 }}>
                    {t('ocrJobCancelConfirmTitle', { defaultValue: 'Cancel job?' })}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: '#444' }}>
                        {t('ocrJobCancelConfirmBody', {
                            defaultValue:
                                'This will stop the job and delete any documents that were already created from it. This action cannot be undone.',
                        })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleAbortConfirm}
                        disabled={Boolean(cancellingId)}
                        sx={{ color: '#555', textTransform: 'none' }}
                    >
                        {t('ocrJobCancelKeep', { defaultValue: 'Keep job' })}
                    </Button>
                    <Button
                        onClick={handleConfirmCancel}
                        disabled={Boolean(cancellingId)}
                        startIcon={
                            cancellingId
                                ? <CircularProgress size={14} thickness={5} sx={{ color: '#fff' }} />
                                : <CancelOutlinedIcon />
                        }
                        sx={{
                            backgroundColor: '#d32f2f',
                            color: '#fff',
                            textTransform: 'none',
                            '&:hover': { backgroundColor: '#b71c1c' },
                            '&.Mui-disabled': { backgroundColor: '#ef9a9a', color: '#fff' },
                        }}
                        variant="contained"
                    >
                        {t('ocrJobCancelConfirm', { defaultValue: 'Cancel & delete' })}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default OcrJobProgress;
